import { HttpClient, httpResource } from '@angular/common/http';
import type { Signal } from '@angular/core';
import { inject, Injectable } from '@angular/core';
import type { Observable } from 'rxjs/internal/Observable';

import type { ODataQueryResult } from '#/app/core/providers/odata-provider/odata-provider.types';
import type {
  CreateRecommendationSetRequest,
  RecommendationSetResponse,
  RecommendationSetsResponse,
} from '#/app/shared/domain/recommendations/recommendation.model';
import { assertIsDefined, isEmptyGuid } from '#/app/shared/domain/types';
import { TenantContext } from '#/app/shared/services/tenant.context';

import { DataInvalidationService } from '../data-invalidation.service';

@Injectable({ providedIn: 'root' })
export class RecommendationsService {
  readonly #httpClient = inject(HttpClient);
  readonly #tenantContext = inject(TenantContext);
  readonly #dataInvalidationService = inject(DataInvalidationService);

  recommendationSetsResource() {
    return httpResource<RecommendationSetsResponse[]>(
      () => {
        this.#dataInvalidationService.track('recommendations');
        const tenantId = this.#tenantContext.currentTenantId();
        return tenantId
          ? `/api/tenants/${tenantId}/recommendations?$select=recommendationSetId,name,isDefault&$orderby=name`
          : undefined;
      },
      {
        parse: (data) =>
          (data as ODataQueryResult<RecommendationSetsResponse>).value,
      },
    );
  }

  recommendationSetResource(recommendationSetId: Signal<string>) {
    return httpResource<RecommendationSetResponse>(() => {
      this.#dataInvalidationService.track('recommendations');
      const tenantId = this.#tenantContext.currentTenantId();
      const recommendationSetIdValue = recommendationSetId();
      return tenantId && !isEmptyGuid(recommendationSetIdValue)
        ? `/api/tenants/${tenantId}/recommendations/${recommendationSetIdValue}`
        : undefined;
    });
  }

  createRecommendationSet(
    request: CreateRecommendationSetRequest,
  ): Observable<RecommendationSetResponse> {
    const tenantId = this.#tenantContext.currentTenantId();
    assertIsDefined(tenantId);

    return this.#httpClient.post<RecommendationSetResponse>(
      `/api/tenants/${tenantId}/recommendations`,
      request,
    );
  }

  updateRecommendationSet(
    recommendationSetId: string,
    request: Omit<CreateRecommendationSetRequest, 'name'>,
  ): Observable<RecommendationSetResponse> {
    const tenantId = this.#tenantContext.currentTenantId();
    assertIsDefined(tenantId);

    return this.#httpClient.put<RecommendationSetResponse>(
      `/api/tenants/${tenantId}/recommendations/${recommendationSetId}`,
      request,
    );
  }

  setDefaultRecommendation(recommendationSetId: string): Observable<never> {
    const tenantId = this.#tenantContext.currentTenantId();
    assertIsDefined(tenantId);

    return this.#httpClient.put<never>(
      `/api/tenants/${tenantId}/recommendations/${recommendationSetId}/set-default`,
      null,
    );
  }

  validateRecommendationName(name: string) {
    const tenantId = this.#tenantContext.currentTenantId();
    assertIsDefined(tenantId);

    return this.#httpClient.get(
      `/api/tenants/${tenantId}/recommendations/validate?name=${encodeURIComponent(name)}`,
    );
  }

  deleteRecommendationSet(recommendationSetId: string): Observable<never> {
    const tenantId = this.#tenantContext.currentTenantId();
    assertIsDefined(tenantId);

    return this.#httpClient.delete<never>(
      `/api/tenants/${tenantId}/recommendations/${recommendationSetId}`,
    );
  }
}
